import { useState, useMemo } from 'react';
import { useSnackbar, SnackbarStatus, Modal } from '@weareopenr/component-library';
import { MultistepWizard } from '../MultistepWizard/MultistepWizard';
import { IWizardStep } from '../MultistepWizard/types';
import ModalImg from 'src/assets/images/create-brand-modal.png';
import { IFormValues } from './types';
import { useMutation } from '@tanstack/react-query';
import { createUser } from 'src/api/createUser/createUser';
import { updateUser } from 'src/api/updateUser/updateUser';
import { IUser } from 'src/types';
import { UserDetailsStep, AccessControlsStep } from './steps';
import { EMAIL_VALIDATION_REGEX } from 'src/constants/regex';
import { useUserTracking } from 'src/hooks';

const UserModal = ({
  show,
  closeModal,
  user,
}: {
  show: boolean;
  closeModal: () => void;
  user?: IUser;
}) => {
  const { showSnackbar } = useSnackbar();
  const { trackEvent, captureException } = useUserTracking();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const mutationFn = (data: IFormValues): Promise<void> => {
    return user ? updateUser(data) : createUser(data);
  };

  const { mutate, isPending } = useMutation({
    mutationFn,
    onSuccess: (_data, variables) => {
      trackEvent(user ? 'user_updated' : 'user_created', {
        role: variables.role,
        brand_count: variables.brands?.includes('*') ? 'all' : variables.brands?.length || 0,
      });
      showSnackbar({
        message: `User ${user ? 'updated' : 'created'} successfully`,
        status: SnackbarStatus.SUCCESS,
      });
      setErrorMessage(null);
      closeModal();
    },
    onError: (error: Error) => {
      setErrorMessage(`Unable to ${user ? 'update' : 'create'} user, please try later`);
      captureException(error);
    },
  });

  const handleComplete = async (data: IFormValues) => {
    mutate(data);
  };

  const steps: IWizardStep<IFormValues>[] = useMemo(() => {
    const allSteps = [
      {
        id: 'user-details',
        title: 'User Details',
        subtitle: 'Enter the user details below',
        component: <UserDetailsStep />,
        validate: (data: IFormValues) => {
          const hasFirstName = !!data.first_name?.trim().length;
          const hasLastName = !!data.last_name?.trim().length;
          const hasValidEmail = !!(data.email && EMAIL_VALIDATION_REGEX.test(data.email));
          return hasFirstName && hasLastName && hasValidEmail;
        },
      },
      {
        id: 'access-controls',
        title: 'Access Controls',
        subtitle: 'Configure role and brand access',
        component: <AccessControlsStep setErrorMessage={setErrorMessage} />,
        validate: (data: IFormValues) => {
          return !!data.role;
        },
      },
    ];

    return user ? [allSteps[1]] : allSteps;
  }, [user, setErrorMessage]);

  return (
    <Modal open={show} closeModal={closeModal} image={ModalImg} imageAlt="Picture of Sides">
      <MultistepWizard<IFormValues>
        steps={steps}
        initialData={{
          id: user?.id || '',
          first_name: user?.first_name || '',
          last_name: user?.last_name || '',
          email: user?.email || '',
          role: user?.role || '',
          brands: user?.brands || [],
        }}
        onComplete={handleComplete}
        onCancel={closeModal}
        isSubmitting={isPending}
        validateOnNavigation={true}
        errorMessage={errorMessage}
      />
    </Modal>
  );
};

export default UserModal;
